import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isThemeColor, readThemeColors } from "@/office/powerpoint/design";
import { allShapes, isPlaceholder, readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionDesignfarben: Lesson = {
  id: "designfarben",
  title: "Designfarben und Designschriften",
  summary: "Warum die obere Hälfte der Farbpalette anders funktioniert als die untere.",
  durationMinutes: 10,
  learningGoals: [
    "Designfarben von Standardfarben unterscheiden",
    "Farben so wählen, dass sie einen Designwechsel überstehen",
    "Designschriften statt einzelner Schriftarten verwenden",
  ],
  steps: [
    {
      id: "die-palette",
      title: "Die zwei Hälften der Farbpalette",
      explanation: [
        "Jede Farbauswahl in PowerPoint ist zweigeteilt. Oben stehen unter " +
          "„Designfarben“ zwölf Felder in fünf Helligkeitsstufen – das sind die Farben " +
          "des aktuellen Designs. Darunter stehen die „Standardfarben“: zehn feste " +
          "Farbtöne, die immer gleich bleiben.",
        "Der Unterschied ist derselbe wie zwischen Formatvorlage und direkter " +
          "Formatierung. Eine Designfarbe ist ein Verweis: „Akzent 1“. Eine Standardfarbe " +
          "ist ein fester Wert: „#FF0000“. Wechselst du das Design, folgt der Verweis, " +
          "der feste Wert nicht.",
        "In der Praxis heißt das: Alles, was aus der oberen Hälfte kommt, bleibt " +
          "stimmig. Alles aus der unteren Hälfte – und erst recht alles aus „Weitere " +
          "Farben“ – ist nach einem Designwechsel ein Fremdkörper. Deshalb lohnt es " +
          "sich, sich anzugewöhnen, ausschließlich in der oberen Hälfte zu klicken.",
        "Wenn die Hausfarben deines Unternehmens gebraucht werden, gehören sie nicht " +
          "einzeln in die Folien, sondern einmal in das Design: „Entwurf“ → " +
          "„Varianten“ → „Farben“ → „Farben anpassen“. Danach stehen sie in der oberen " +
          "Hälfte der Palette und verhalten sich wie jede andere Designfarbe.",
      ],
      keyPoints: [
        "Obere Hälfte der Palette: Designfarben, Verweise, folgen dem Design.",
        "Untere Hälfte: Standardfarben, feste Werte, überleben keinen Designwechsel.",
        "Entwurf → Varianten → Farben → Farben anpassen für eigene Hausfarben.",
      ],
      task: {
        instruction:
          "Sieh die Textfarben in deinen Platzhaltern durch: Sie sollen aus den " +
          "Designfarben stammen, nicht aus den Standardfarben.",
        hint: "Am einfachsten: Text markieren und in der Farbpalette eine Farbe aus der oberen Hälfte wählen.",
        check: async () => {
          const snapshot = await readPresentation();
          const farben = await readThemeColors();

          if (farben === null) {
            return nichtPruefbar(
              "Die Farben des Designs",
              "1.10",
              "Markiere den Text und sieh in der Farbpalette nach, ob die gewählte Farbe in der oberen Hälfte steht.",
            );
          }
          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Die Schriftfarbe im Textrahmen",
              "1.4",
              "Prüfe die Farbauswahl selbst in der Farbpalette.",
            );
          }

          // Ohne eigene Farbe liefert PowerPoint die Farbe aus dem Layout –
          // die ist per Definition eine Designfarbe. Geprüft wird deshalb nur,
          // was ausdrücklich gesetzt wurde.
          const gesetzt = allShapes(snapshot).filter(
            (form) => isPlaceholder(form) && form.paragraphs.length > 0 && form.font?.color,
          );
          const fremd = gesetzt.filter((form) => !isThemeColor(form.font?.color ?? null, farben));

          if (gesetzt.length === 0) {
            return pass(
              "Es ist keine eigene Textfarbe gesetzt – die Platzhalter nehmen die Farbe aus dem Design. Genau so ist es richtig.",
            );
          }
          if (fremd.length === 0) {
            return pass(
              `Alle ${gesetzt.length} gesetzten Textfarben stammen aus der Designpalette – ein Designwechsel nimmt sie mit.`,
            );
          }

          return fail(
            `Auf Folie ${fremd[0].slideIndex + 1} steht Text in der Farbe ${fremd[0].font?.color}, die nicht zur Designpalette gehört.` +
              (fremd.length > 1 ? ` Insgesamt betrifft das ${fremd.length} Platzhalter.` : ""),
            "Text markieren und eine Farbe aus der oberen Hälfte der Palette wählen. Soll es genau diese Farbe sein, " +
              "nimm sie über „Entwurf“ → „Varianten“ → „Farben“ → „Farben anpassen“ ins Design auf.",
          );
        },
      },
    },
    {
      id: "designschriften",
      title: "Designschriften",
      explanation: [
        "Neben den Farben bringt ein Design zwei Schriftarten mit: eine für " +
          "Überschriften und eine für den Fließtext. In der Schriftartenliste stehen sie " +
          "ganz oben unter „Designschriftarten“, mit dem Zusatz „(Überschriften)“ " +
          "beziehungsweise „(Textkörper)“.",
        "Wer von dort auswählt, formatiert nicht mit einer Schriftart, sondern mit einer " +
          "Rolle. Das hat denselben Vorteil wie bei den Farben – und einen zweiten, den " +
          "man erst bemerkt, wenn es schiefgeht: Eine Präsentation, die auf einem fremden " +
          "Rechner geöffnet wird, findet die verwendete Schriftart dort möglicherweise " +
          "nicht. PowerPoint ersetzt sie dann, und das ganze Layout verschiebt sich.",
        "Zwei Absicherungen gibt es dagegen. Erstens: Schriftarten wählen, die überall " +
          "vorhanden sind – Calibri, Arial, Segoe UI, Times New Roman. Zweitens: die " +
          "Schriftarten in die Datei einbetten, über „Datei“ → „Optionen“ → " +
          "„Speichern“ → „Schriftarten in der Datei einbetten“. Das vergrößert die Datei, " +
          "sichert aber das Aussehen.",
        "Für Präsentationen, die auf einem fremden Gerät laufen sollen, ist die " +
          "verlässlichste Absicherung ohnehin eine andere: eine PDF mitnehmen. Dazu mehr " +
          "im Modul „Export und Weitergabe“.",
      ],
      keyPoints: [
        "Designschriftarten stehen in der Schriftartenliste ganz oben.",
        "Datei → Optionen → Speichern → Schriftarten in der Datei einbetten.",
        "Für fremde Geräte zusätzlich eine PDF mitnehmen.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass in den Platzhaltern deiner Folien höchstens zwei " +
          "verschiedene Schriftarten vorkommen – die beiden des Designs.",
        hint: "Am einfachsten: Text markieren und in der Schriftartenliste ganz oben unter „Designschriftarten“ auswählen.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Die Schriftart im Textrahmen",
              "1.4",
              "Klick nacheinander in deine Textbereiche und vergleiche die Schriftartenanzeige.",
            );
          }

          const platzhalter = allShapes(snapshot).filter(
            (form) => isPlaceholder(form) && form.paragraphs.length > 0,
          );
          if (platzhalter.length === 0) {
            return fail(
              "In keinem Platzhalter steht Text.",
              "Schreibe zuerst Text in die Platzhalter.",
            );
          }

          const schriften = new Map<string, number[]>();
          for (const form of platzhalter) {
            const name = form.font?.name;
            if (!name) continue;
            schriften.set(name, [...(schriften.get(name) ?? []), form.slideIndex + 1]);
          }

          if (schriften.size === 0) {
            return fail(
              "Für keinen Platzhalter ließ sich eine eindeutige Schriftart auslesen – der Text ist gemischt formatiert.",
              "Markiere den Text und wähle in der Schriftartenliste oben eine Designschriftart. Alternativ „Start“ → „Zurücksetzen“.",
            );
          }
          if (schriften.size <= 2) {
            return pass(
              `Im Einsatz sind ${schriften.size === 1 ? "eine Schriftart" : "zwei Schriftarten"}: ${[...schriften.keys()].join(" und ")}.`,
            );
          }

          const verteilung = [...schriften.entries()]
            .map(([name, folien]) => `${name} (Folie ${[...new Set(folien)].join(", ")})`)
            .join("; ");
          return fail(
            `Es sind ${schriften.size} verschiedene Schriftarten im Einsatz: ${verteilung}.`,
            "Markiere den abweichenden Text und wähle oben unter „Designschriftarten“ die passende Rolle – „(Überschriften)“ oder „(Textkörper)“.",
          );
        },
      },
    },
  ],
};
